import { useState, useEffect } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { useUpload } from "@/hooks/use-upload";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormDescription,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useToast } from "@/hooks/use-toast";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { Plus, Pencil, Package, Upload, X, ImageIcon, Trash2, Tags, Save } from "lucide-react";
import type { PriceList, PriceListPrice } from "@shared/schema";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import type { Product } from "@shared/schema";

const productFormSchema = z.object({
  name: z.string().min(1, "Namn krävs"),
  description: z.string().optional(),
  weight: z.string().optional(),
  doughWeightPerLiter: z.string().optional(),
  pcsPerBag: z.coerce.number().min(1).default(1),
  bagsPerBox: z.coerce.number().min(1).default(1),
  sellUnit: z.enum(["pcs", "bag", "box"]),
  unitPrice: z.string().min(1, "Pris krävs"),
  isActive: z.boolean().default(true),
  isNew: z.boolean().default(false),
});

type ProductFormData = z.infer<typeof productFormSchema>;

export default function AdminProducts() {
  const { toast } = useToast();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [deleteProduct, setDeleteProduct] = useState<Product | null>(null);
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [productPriceEdits, setProductPriceEdits] = useState<Record<string, string>>({});
  const { uploadFile, isUploading } = useUpload({
    onSuccess: (response) => {
      const publicUrl = `/api/uploads${response.objectPath}`;
      setImageUrl(publicUrl);
      toast({ title: "Bild uppladdad!" });
    },
    onError: () => {
      toast({ title: "Fel", description: "Kunde inte ladda upp bilden.", variant: "destructive" });
    },
  });

  const { data: products, isLoading } = useQuery<Product[]>({
    queryKey: ["/api/admin/products"],
  });

  const { data: allPriceLists } = useQuery<PriceList[]>({
    queryKey: ["/api/admin/price-lists"],
  });

  const nonDefaultPriceLists = allPriceLists?.filter(pl => !pl.isDefault) || [];

  const { data: productPrices } = useQuery<(PriceListPrice & { priceListName: string })[]>({
    queryKey: ["/api/admin/products", editingProduct?.id, "prices"],
    enabled: !!editingProduct,
  });

  useEffect(() => {
    if (editingProduct && productPrices && nonDefaultPriceLists.length > 0) {
      const edits: Record<string, string> = {};
      for (const pl of nonDefaultPriceLists) {
        const existing = productPrices.find(p => p.priceListId === pl.id);
        edits[pl.id] = existing ? existing.price : "";
      }
      setProductPriceEdits(edits);
    }
  }, [editingProduct?.id, productPrices, nonDefaultPriceLists.length]);

  const saveProductPricesMutation = useMutation({
    mutationFn: async (priceListId: string) => {
      const price = productPriceEdits[priceListId];
      return apiRequest("PUT", `/api/admin/price-lists/${priceListId}/prices`, {
        prices: [{ productId: editingProduct!.id, price: price === "" ? null : price }],
      });
    },
    onSuccess: () => {
      toast({ title: "Pris sparat!" });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/products", editingProduct?.id, "prices"] });
    },
  });

  const form = useForm<ProductFormData>({
    resolver: zodResolver(productFormSchema),
    defaultValues: {
      name: "",
      description: "",
      weight: "",
      doughWeightPerLiter: "",
      pcsPerBag: 1,
      bagsPerBox: 1,
      sellUnit: "pcs",
      unitPrice: "",
      isActive: true,
    },
  });

  const createMutation = useMutation({
    mutationFn: (data: ProductFormData) => {
      const payload = { 
        ...data, 
        imageUrl: imageUrl || undefined,
        weight: data.weight || undefined,
        doughWeightPerLiter: data.doughWeightPerLiter || undefined,
        description: data.description || undefined,
      };
      return apiRequest("POST", "/api/admin/products", payload);
    },
    onSuccess: () => {
      toast({ title: "Produkt skapad!" });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/products"] });
      queryClient.invalidateQueries({ queryKey: ["/api/products"] });
      setDialogOpen(false);
      setImageUrl(null);
      form.reset();
    },
    onError: () => {
      toast({ title: "Fel", description: "Kunde inte skapa produkt.", variant: "destructive" });
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: ProductFormData & { id: string }) => {
      const payload = { 
        ...data, 
        imageUrl: imageUrl || undefined,
        weight: data.weight || undefined,
        doughWeightPerLiter: data.doughWeightPerLiter || undefined,
        description: data.description || undefined,
      };
      return apiRequest("PATCH", `/api/admin/products/${data.id}`, payload);
    },
    onSuccess: () => {
      toast({ title: "Produkt uppdaterad!" });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/products"] });
      queryClient.invalidateQueries({ queryKey: ["/api/products"] });
      setDialogOpen(false);
      setEditingProduct(null);
      setImageUrl(null);
      form.reset();
    },
    onError: () => {
      toast({ title: "Fel", description: "Kunde inte uppdatera produkt.", variant: "destructive" });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiRequest("DELETE", `/api/admin/products/${id}`),
    onSuccess: () => {
      toast({ title: "Produkt borttagen!" });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/products"] });
      queryClient.invalidateQueries({ queryKey: ["/api/products"] });
      setDeleteProduct(null);
    },
    onError: (error: Error) => {
      toast({ 
        title: "Fel", 
        description: error.message || "Kunde inte ta bort produkt.", 
        variant: "destructive" 
      });
      setDeleteProduct(null);
    },
  });

  const openCreate = () => {
    setEditingProduct(null);
    setImageUrl(null);
    form.reset({
      name: "",
      description: "",
      weight: "",
      doughWeightPerLiter: "",
      pcsPerBag: 1,
      bagsPerBox: 1,
      sellUnit: "pcs",
      unitPrice: "",
      isActive: true,
      isNew: false,
    });
    setDialogOpen(true);
  };

  const openEdit = (product: Product) => {
    setEditingProduct(product);
    setImageUrl(product.imageUrl || null);
    form.reset({
      name: product.name,
      description: product.description || "",
      weight: product.weight || "",
      doughWeightPerLiter: product.doughWeightPerLiter || "",
      pcsPerBag: product.pcsPerBag || 1,
      bagsPerBox: product.bagsPerBox || 1,
      sellUnit: (product.sellUnit as "pcs" | "bag" | "box") || "pcs",
      unitPrice: product.unitPrice || "",
      isActive: product.isActive ?? true,
      isNew: product.isNew ?? false,
    });
    setDialogOpen(true);
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      await uploadFile(file);
    }
  };

  const onSubmit = (data: ProductFormData) => {
    if (editingProduct) {
      updateMutation.mutate({ ...data, id: editingProduct.id });
    } else {
      createMutation.mutate(data);
    }
  };

  const getUnitLabel = (unit: string) => {
    switch (unit) {
      case "pcs": return "st";
      case "bag": return "påse";
      case "box": return "låda";
      default: return unit;
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold">Produkter</h1>
          <p className="text-muted-foreground">Hantera dina produkter och förpackningar</p>
        </div>
        <Button onClick={openCreate} data-testid="button-add-product">
          <Plus className="h-4 w-4 mr-2" />
          Lägg till produkt
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Produktlista</CardTitle>
          <CardDescription>Alla produkter i systemet</CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-2">
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : products && products.length > 0 ? (
            <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-16">Bild</TableHead>
                  <TableHead>Namn</TableHead>
                  <TableHead>Vikt</TableHead>
                  <TableHead>Förpackning</TableHead>
                  <TableHead>Säljs som</TableHead>
                  <TableHead>Pris</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Åtgärd</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {products.map((product) => (
                  <TableRow key={product.id}>
                    <TableCell>
                      {product.imageUrl ? (
                        <img
                          src={product.imageUrl}
                          alt={product.name}
                          className="h-10 w-10 object-cover rounded"
                        />
                      ) : (
                        <div className="h-10 w-10 flex items-center justify-center rounded bg-muted">
                          <ImageIcon className="h-5 w-5 text-muted-foreground" />
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-2 flex-wrap">
                        {product.name}
                        {product.isNew && <Badge variant="default" data-testid={`badge-new-${product.id}`}>Nyhet</Badge>}
                      </div>
                    </TableCell>
                    <TableCell>{product.weight || "-"}</TableCell>
                    <TableCell>
                      {product.pcsPerBag} st/påse, {product.bagsPerBox} påsar/låda
                    </TableCell>
                    <TableCell>{getUnitLabel(product.sellUnit)}</TableCell>
                    <TableCell>{product.unitPrice} kr (ink.)/{getUnitLabel(product.sellUnit)}</TableCell>
                    <TableCell>
                      <Badge variant={product.isActive ? "default" : "secondary"}>
                        {product.isActive ? "Aktiv" : "Inaktiv"}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={() => openEdit(product)}
                          data-testid={`button-edit-${product.id}`}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={() => setDeleteProduct(product)}
                          data-testid={`button-delete-${product.id}`}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            </div>
          ) : (
            <div className="py-12 text-center">
              <Package className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">Inga produkter ännu</p>
              <Button className="mt-4" onClick={openCreate}>
                <Plus className="h-4 w-4 mr-2" />
                Lägg till första produkten
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md p-0">
          <DialogHeader className="px-4 pt-4 pb-2">
            <DialogTitle>
              {editingProduct ? "Redigera produkt" : "Lägg till produkt"}
            </DialogTitle>
          </DialogHeader>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)}>
              <div className="max-h-[60vh] overflow-y-auto px-4">
                <div className="space-y-3 pb-4">
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Produktnamn</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="T.ex. Vetebröd" data-testid="input-product-name" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Beskrivning</FormLabel>
                    <FormControl>
                      <Textarea {...field} placeholder="Valfri beskrivning" data-testid="input-product-description" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="space-y-2">
                <label className="text-sm font-medium">Produktbild</label>
                <div className="flex items-center gap-4">
                  {imageUrl ? (
                    <div className="relative">
                      <img
                        src={imageUrl}
                        alt="Produktbild"
                        className="h-20 w-20 object-cover rounded-md border"
                      />
                      <Button
                        type="button"
                        size="icon"
                        variant="destructive"
                        className="absolute -top-2 -right-2 h-6 w-6"
                        onClick={() => setImageUrl(null)}
                        data-testid="button-remove-image"
                      >
                        <X className="h-3 w-3" />
                      </Button>
                    </div>
                  ) : (
                    <div className="h-20 w-20 flex items-center justify-center rounded-md border border-dashed bg-muted">
                      <ImageIcon className="h-8 w-8 text-muted-foreground" />
                    </div>
                  )}
                  <div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={isUploading}
                      onClick={() => document.getElementById("image-upload")?.click()}
                      data-testid="button-upload-image"
                    >
                      <Upload className="h-4 w-4 mr-2" />
                      {isUploading ? "Laddar upp..." : "Ladda upp bild"}
                    </Button>
                    <input
                      id="image-upload"
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={handleImageUpload}
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      Max 10 MB. JPG, PNG eller WebP.
                    </p>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="weight"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Vikt per enhet (g)</FormLabel>
                      <FormControl>
                        <Input {...field} type="number" step="0.01" placeholder="T.ex. 100" data-testid="input-product-weight" />
                      </FormControl>
                      <FormDescription>Gram per styck</FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="doughWeightPerLiter"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Deg vikt per liter (g)</FormLabel>
                      <FormControl>
                        <Input {...field} type="number" step="0.01" placeholder="T.ex. 1200" data-testid="input-dough-weight-per-liter" />
                      </FormControl>
                      <FormDescription>Gram deg per liter</FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="unitPrice"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Pris (kr)</FormLabel>
                    <FormControl>
                      <Input {...field} type="number" step="0.01" placeholder="0.00" data-testid="input-product-price" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="pcsPerBag"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Styck per påse</FormLabel>
                      <FormControl>
                        <Input {...field} type="number" min={1} data-testid="input-pcs-per-bag" />
                      </FormControl>
                      <FormDescription>T.ex. 4 st i en påse</FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="bagsPerBox"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Påsar per låda</FormLabel>
                      <FormControl>
                        <Input {...field} type="number" min={1} data-testid="input-bags-per-box" />
                      </FormControl>
                      <FormDescription>T.ex. 12 påsar i en låda</FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="sellUnit"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Säljs som</FormLabel>
                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                      <FormControl>
                        <SelectTrigger data-testid="select-sell-unit">
                          <SelectValue placeholder="Välj enhet" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="pcs">Styck (st)</SelectItem>
                        <SelectItem value="bag">Påse</SelectItem>
                        <SelectItem value="box">Låda</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

                  <FormField
                    control={form.control}
                    name="isActive"
                    render={({ field }) => (
                      <FormItem className="flex items-center justify-between rounded-lg border p-3">
                        <div className="space-y-0.5">
                          <FormLabel>Aktiv</FormLabel>
                          <FormDescription>Produkten visas för kunder</FormDescription>
                        </div>
                        <FormControl>
                          <Switch
                            checked={field.value}
                            onCheckedChange={field.onChange}
                            data-testid="switch-is-active"
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="isNew"
                    render={({ field }) => (
                      <FormItem className="flex items-center justify-between rounded-lg border p-3">
                        <div className="space-y-0.5">
                          <FormLabel>Nyhet</FormLabel>
                          <FormDescription>Visa "Nyhet" tagg för kunder</FormDescription>
                        </div>
                        <FormControl>
                          <Switch
                            checked={field.value}
                            onCheckedChange={field.onChange}
                            data-testid="switch-is-new"
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                {editingProduct && nonDefaultPriceLists.length > 0 && (
                  <div className="space-y-3 rounded-lg border p-3">
                    <div className="flex items-center gap-2">
                      <Tags className="h-4 w-4" />
                      <p className="text-sm font-medium">Prislistor</p>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Standardpris: {form.watch("unitPrice") || editingProduct.unitPrice} kr. Ange specialpriser nedan.
                    </p>
                    {nonDefaultPriceLists.map(pl => (
                      <div key={pl.id} className="flex items-center gap-2">
                        <label className="text-sm flex-1 text-muted-foreground">{pl.name}</label>
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          placeholder="Standardpris"
                          value={productPriceEdits[pl.id] || ""}
                          onChange={e => setProductPriceEdits(prev => ({ ...prev, [pl.id]: e.target.value }))}
                          className="w-28 text-right"
                          data-testid={`input-product-price-${pl.id}`}
                        />
                        <Button
                          type="button"
                          size="icon"
                          variant="ghost"
                          onClick={() => saveProductPricesMutation.mutate(pl.id)}
                          disabled={saveProductPricesMutation.isPending}
                          data-testid={`button-save-price-${pl.id}`}
                        >
                          <Save className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
                </div>
              </div>
              <DialogFooter className="px-4 py-3 border-t">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Avbryt
                </Button>
                <Button
                  type="submit"
                  disabled={createMutation.isPending || updateMutation.isPending}
                  data-testid="button-save-product"
                >
                  {createMutation.isPending || updateMutation.isPending ? "Sparar..." : "Spara"}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteProduct} onOpenChange={(open) => !open && setDeleteProduct(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Ta bort produkt?</AlertDialogTitle>
            <AlertDialogDescription>
              Är du säker på att du vill ta bort "{deleteProduct?.name}"? Detta går inte att ångra.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Avbryt</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteProduct && deleteMutation.mutate(deleteProduct.id)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteMutation.isPending ? "Tar bort..." : "Ta bort"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
